import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useFilters, useGlobalFilter, usePagination, useSortBy, useTable} from "react-table";
import {FormProvider, useForm} from "react-hook-form";
import {useSearchParams} from "../../hooks/useSearchParams";
import {useLocation} from "react-router-dom";
import {getHederstegn} from "../../../data/data";
import SelectStyled from "../../inputs/SelectStyled";
import Select from "react-select";
import {Image} from "semantic-ui-react";
import styled from "styled-components";
import {device} from "../../../theme";
import InputWithLabel from "../../inputs/InputWithLabel";
import HederstegnTable from "./HederstegnTable";
import {Menu} from "../../layout/Menu";

const dataAll = getHederstegn();

export const Hederstegn = () => {
    const methods = useForm();
    const {setParam, updateParam, queryParams} = useSearchParams();
    const location = useLocation();
    const filterFields = useMemo(() => ["club", "distinctions", "titles"], [])
    const [year, setYear] = useState("");
    const [localDistinctions, setLocalDistinctions] = useState();
    const [localClub, setLocalClub] = useState();
    const ref = useRef();

    let distinctions = methods.watch("distinctions");
    useEffect(() => {
        resetAllFilters();

        queryParams.forEach((value, key) => {
            if (key === "search") {
                setGlobalFilter(value);
                methods.setValue("search", value);
                updateParam("search", value);
                return;
            }
            if (key === "year") {
                setYear(value)
            }
            if (!filterFields.includes(key)) return;
            setFilter(key, value || undefined);
            if (key === "distinctions") {
                methods.setValue("distinctions", value);
            }
            if (key === "titles") {
                methods.setValue("titles", value);
            }

        });
    }, [location]);

    function resetAllFilters() {
        setAllFilters([]);
    }

    const data = useMemo(() => {
            return dataAll.filter(d => true).map(d => ({...d, titles: ""}));
        }
        , [dataAll])

    const years = useMemo(() => {
        return [{
            label: "Alle",
            value: ""
        }, ...[...new Set(data.map(d => d.distinctions).flatMap(t => t).map(d => d.year))].sort((a, b) => b - a)
            .map(y => ({label: `${y}`, value: y}))]
    }, [data])

    const columns = useMemo(
        () => [
            {
                Header: "Navn",
                accessor: "name",
                disableFilters: true,
            },
            {
                Header: "Klubb",
                accessor: "club",
                Filter: SelectClubFilter,
                filter: "exact"
            },
            {
                Header: "ANSFF",
                accessor: "ansff",
                disableFilters: true
            },
            {
                Header: "ENSFF",
                accessor: "ensff",
                disableFilters: true
            },
            {
                Header: "Nasjonal plakett",
                accessor: "nasjonalplakett",
                disableFilters: true
            },
            {
                Header: "Internasjonal plakett",
                accessor: "internasjonalplakett",
                disableFilters: true
            },
            {
                Header: "Titler",
                accessor: "titles",
                disableFilters: true,
                Cell: ({value, row}) => {
                    const distinctions = year ? row.values.distinctions.filter(d => +d.year <= +year) : row.values.distinctions;
                    return <>{distinctionsToText(distinctions)}</>
                }
            },
            {
                Header: "Hederstegn",
                accessor: "distinctions",
                Filter: SelectDistinctionsFilter,
                filter: (rows, id, filterValue) => {
                    return rows.filter(r => r.values.distinctions.some(d => d.name === filterValue));
                },
                Cell: ({
                           value,
                           row
                       }) => (distinctions || year ? value.map(v => [v.id, v.order]) : Object.entries(value.reduce((result, item) => {
                    if (!(item.id in result))
                        result[item.id] = item.order;
                    else if (result[item.id] < item.order)
                        result[item.id] = item.order;
                    return result;
                }, {})))
                    .map(t => value.find(v => v.id === t[0] && v.order === t[1]))
                    .filter(v => distinctions ? v.name === distinctions : true)
                    .filter(v => year ? +v.year === +year : true)
                    .map(v => <>
                        {v.name} - {v.year}<br/>
                    </>)
            }
        ], [data, distinctions, year]
    )

    const search = methods.watch("search");

    const initialState = useMemo(() => {
        const filtersFromUrl = [...queryParams.entries()]
            .filter((entry) => filterFields.includes[entry[0]])
            .map((entry) => ({id: entry[0], value: entry[1] || undefined}));
        return {
            pageSize: 10,
            filters: filtersFromUrl,
            sortBy: [
                {
                    id: 'year',
                    desc: true
                },
                {
                    id: 'award',
                    asc: true
                }
            ]
        }
    }, []);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        rows,

        previousPage,
        canPreviousPage,
        nextPage,
        canNextPage,
        gotoPage,
        pageOptions,
        pageCount,

        setFilter,
        setAllFilters,
        setGlobalFilter,
        state: {pageIndex},
    } = useTable(
        {columns, data, initialState},
        useGlobalFilter,
        useFilters,
        useSortBy,
        usePagination
    );

    function SelectClubFilter({column: {filterValue, preFilteredRows, id}}) {
        const options = useMemo(() => {
            const values = [...new Set(preFilteredRows.map((row) => row.values[id]))];
            let options;

            options = values
                .filter((item) => !!item)
                ?.map((item) => ({
                    value: item,
                    label: item,
                }))
                .sort((a, b) => -a.label?.localeCompare(b.label));
            return [{label: "Alle", value: ""}, ...options];
        }, [id, preFilteredRows]);
        const currentValue = options.find((item) => item.value === filterValue);

        return (
            <SelectStyled
                style={{minWidth: "12rem", marginTop: "0.2rem", marginBottom: "0.2rem"}}
            >
                <Select
                    defaultValue={options[0]}
                    value={currentValue || undefined}
                    onChange={(e) => setLocalClub(e.value)}
                    options={options}
                    classNamePrefix="rs"
                    name={id}
                    menuPortalTarget={document.body}
                    styles={{menuPortal: (base) => ({...base, zIndex: 9999})}}
                />
            </SelectStyled>
        );
    }

    function SelectDistinctionsFilter({column: {filterValue, preFilteredRows, id}}) {
        const options = useMemo(() => {
            const values = [...new Set(preFilteredRows.map((row) => row.values[id]))];

            const options = [...new Set(values
                .filter(item => !!item)
                .flatMap(f => f)
                .map(item => item.name))]
                ?.map(item => ({
                    value: item,
                    label: item,
                }))
                .sort((a, b) => -a.label?.localeCompare(b.label));
            return [{label: "Alle", value: ""}, ...options];
        }, [id, preFilteredRows]);
        const currentValue = options.find((item) => item.value === filterValue);
        return (
            <SelectStyled
                style={{minWidth: "12rem", marginTop: "0.2rem", marginBottom: "0.2rem"}}
            >
                <Select
                    defaultValue={options[0]}
                    value={currentValue || undefined}
                    onChange={(e) => setLocalDistinctions(e.value)}
                    options={options}
                    classNamePrefix="rs"
                    name={id}
                    menuPortalTarget={document.body}
                    styles={{menuPortal: (base) => ({...base, zIndex: 9999})}}
                />
            </SelectStyled>
        );
    }

    useEffect(() => {
        if (search !== undefined) {
            setParam("search", search)
        }
    }, [search]);

    useEffect(() => {
        if( localDistinctions !== undefined) {
            setParam("distinctions", localDistinctions)
        }
    }, [localDistinctions])

    useEffect(() => {
        if (localClub !== undefined) {
            setParam("club", localClub)
        }
    }, [localClub])

    useEffect(() => {
        if (year !== undefined && year !== null && year!=="") {
            setParam("year", year);
        }
    }, [year])

    const distinctionsToText = (distinctions) => {
        const findHighest = (id, notIn = [], num = 0) => {
            const res = distinctions.filter(
                d => d.id === id &&
                    !notIn.includes(d.order) &&
                    (num === 0 ? true : d.order === num)
            )
                .sort((a, b) => a.order - b.order)
            if (res.length === 0) return null;
            return res[res.length - 1]
        }
        const findHighestIn = (id, list = []) => {
            const res = distinctions.filter(
                d => d.id === id &&
                    list.includes(d.order)
            )
                .sort((a, b) => a.order - b.order)
            if (res.length === 0) return null;
            return res[res.length - 1]
        }
        const result = [];
        if (findHighest('8214cc4e-3d9c-42b5-bf1d-57545594ea23'))
            result.push("HonENSFF");
        if (findHighest('77c488bc-219a-4f21-a00f-6a14b97d13f1'))
            result.push("ANSFF")
        const ensff = findHighest('2361f08e-7548-463e-a7c4-da72fa4e384e');
        if (ensff) {
            const convert = {
                "Bronse": "B",
                "Sølv": "S",
                "Gull": "G",
                "Platina": "P",
                "D1": "D1",
                "D2": "D2",
                "D3": "D3"
            }
            const type = convert[ensff.name.split(" ")[0].split("-")[1]];
            let name = `ENSFF${type ? "-" + type : ""}`;
            result.push(name)
        }
        if (findHighest('d0541f3a-8ca1-4297-add2-8b0bb0b83e31'))
            result.push("MNSFF")
        if (findHighest("a7bdff40-3f73-46b5-97f9-118f58f731ab", [], 8))
            result.push("HonFIAP")
        const fiap = findHighest('a7bdff40-3f73-46b5-97f9-118f58f731ab', [6, 7, 8]);
        if (fiap)
            result.push(fiap.name.split(" ")[0])
        if (findHighest('a7bdff40-3f73-46b5-97f9-118f58f731ab', [], 7))
            result.push("ESFIAP")
        if (findHighest('a7bdff40-3f73-46b5-97f9-118f58f731ab', [], 6))
            result.push("MFIAP")
        const honpsa = findHighestIn('cf836bee-aad2-4d52-9a3c-82f40e6fe3ac', [7, 8, 9, 10])
        if (honpsa)
            result.push(honpsa.name.split(" ")[0])
        const psa = findHighest('cf836bee-aad2-4d52-9a3c-82f40e6fe3ac', [7, 8, 9, 10, 11, 12, 13])
        if (psa)
            result.push(psa.name.split(" ")[0])
        const ppsa = findHighest('cf836bee-aad2-4d52-9a3c-82f40e6fe3ac', [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10])
        if (ppsa)
            result.push(ppsa.name.split(" ")[0])
        if (findHighest('0c85c21d-1027-4f4b-9362-0a92ae9a2233', [], 2))
            result.push("HonENFFF")
        const nsmif = findHighestIn('0c85c21d-1027-4f4b-9362-0a92ae9a2233', [3, 4, 5, 6, 7, 8, 9])
        if (nsmif)
            result.push(nsmif.name.split(" ")[0])
        const mnfff = findHighestIn('0c85c21d-1027-4f4b-9362-0a92ae9a2233', [10, 11, 12]);
        if (mnfff)
            result.push(mnfff.name)
        const mnffftheme = findHighest('2ccfe398-2eC2-4a40-b516-ef85ec3e5300')
        if (mnffftheme)
            result.push("MNFFF/THEME")
        const gpucr = findHighest('5fc77fcf-2e55-4c42-9614-2d43634fd060');
        if (gpucr)
            result.push(`GPU-CR${gpucr.name.at(-1)}`)
        const gputitle = findHighest('402d6ee4-40aa-4790-bf1e-e74aa58eba76');
        const gpuvip = findHighest('ad770f5e-9128-4dc6-bf57-1724e3dff995');
        if (gpuvip)
            result.push(`GPU-VIP${gpuvip.name.at(-1)}`)
        if (gputitle)
            result.push(gputitle.name.replace(" ", "-"))
        return result.join(", ");
    }

    return <>
        <Header>
            <div>
                <a href="https://nsff.no"><Image src={"/resultater/images/logo-darkgreen.png"} alt="Logo"
                                                 height={"60"}/></a>
            </div>
            <div>
                Hederstegn NSFF
            </div>
            <div>

            </div>
        </Header>
        <Menu/>
        <MainArea>
            <Container>
                <FormProvider {...methods}>
                    <SearchInput
                        name="search"
                        label={"Skriv inn navn"}
                        autoComplete="off"
                    />
                    Filtrer på år
                    <SelectStyled className={"year"}>
                        <Select
                            defaultValue={years[0]}
                            value={{label: `${year}`, value: year}}
                            onChange={(e) => {
                                setYear(e.value);
                            }}
                            options={years}
                            classNamePrefix="rs"
                            name={"year"}
                            menuPortalTarget={document.body}
                            styles={{menuPortal: (base) => ({...base, zIndex: 9999})}}
                        />
                    </SelectStyled>
                    <HederstegnTable
                        getTableProps={getTableProps}
                        headerGroups={headerGroups}
                        getTableBodyProps={getTableBodyProps}
                        page={page}
                        prepareRow={prepareRow}
                        pageCount={pageCount}
                        previousPage={previousPage}
                        canPreviousPage={canPreviousPage}
                        nextPage={nextPage}
                        canNextPage={canNextPage}
                        pageOptions={pageOptions}
                        gotoPage={gotoPage}
                        pageIndex={pageIndex}
                        isLoading={false}
                        rows={rows}
                    />
                </FormProvider>
            </Container>
        </MainArea>
    </>
}

const Container = styled.div`
  opacity: 0;
  animation: fadeIn 300ms ease forwards;
  
  .year {
    width: 150px;
    
    @media ${device.mobile} {
      width: 100%;
    }
  }
`;

const MainArea = styled.div`
  padding: 1.25rem 2.5rem 3.125rem 3.125rem;

  @media ${device.mobile} {
    padding: 1.25rem 0.625rem 3.125rem 0.625rem;
  }
`;

const SearchInput = styled(InputWithLabel)`
  flex-grow: 1;
  max-width: 27rem;
  margin-right: 1rem;
`;

const Header = styled.div`
  height: 70px;
  background-color: #90b361;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  color: #36501c;
  font-family: 'Fibon Neue';

  > div {
    display: flex;
    flex-direction: row;
    margin-left: 10px;

    > div {
      margin-left: 20px;
    }

    align-items: center;
  }

  > div:nth-child(2) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    font-size: 1.4rem;
  }

  > div:nth-child(3) {
    margin-right: 20px;
  }
`
